import { useEffect, useRef, type ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';

// The 100vh default belongs to hosts that render outside any chrome (the
// entry guard, AuthGuard). Hosts inside a padded shell must pass the reduced
// height, or the page overflows by the shell's own padding.
type FullPageMinHeight = { minHeight?: string };

export const FullPageStatus = ({ message, minHeight = '100vh' }: { message: ReactNode } & FullPageMinHeight) => (
  <Box
    role='status'
    aria-live='polite'
    sx={{
      minHeight,
      display: 'grid',
      placeItems: 'center',
    }}
  >
    <Stack spacing={1.5} alignItems='center'>
      <CircularProgress aria-hidden size={28} />
      <Typography variant='body2' color='text.secondary'>
        {message}
      </Typography>
    </Stack>
  </Box>
);

// Focus lands here when a session gate resolves after having shown its error
// surface: the error Alert unmounts on success, and without an anchor focus
// drops to <body>, leaving keyboard and screen-reader users unaware the retry
// succeeded. Render it just before the recovered content.
export const SessionRecoveryFocusAnchor = ({ label }: { label: string }) => {
  const anchorRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    anchorRef.current?.focus();
  }, []);
  return (
    <Typography component='h2' ref={anchorRef} sx={visuallyHidden} tabIndex={-1}>
      {label}
    </Typography>
  );
};

type FullPageErrorProps = {
  message: ReactNode;
  retrying: boolean;
  onRetry: () => void;
  // Overrides the Retry label when the primary action navigates instead of
  // retrying - copy must describe what the button actually does.
  actionLabel?: string;
  // Escape hatch (navigation, sign-out): these surfaces render outside
  // MainLayout with no navbar or sidebar, so a failing Retry must never be
  // the only interactive element on the page.
  secondaryAction?: ReactNode;
  // Count of failed attempts. The visible copy stays stable, but the change
  // in the visually hidden counter makes role='alert' re-announce, so a
  // failed retry is distinguishable from a no-op for assistive tech.
  attempt?: number;
  // 'error' is for failures (guards, auth); 'info' is for succeeding
  // operations that still need the persistent Retry affordance (sign-out).
  severity?: 'error' | 'info';
} & FullPageMinHeight;

export const FullPageError = ({
  message,
  retrying,
  onRetry,
  actionLabel,
  secondaryAction,
  attempt,
  minHeight = '100vh',
  severity = 'error',
}: FullPageErrorProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight,
        display: 'grid',
        placeItems: 'center',
        p: 3,
      }}
    >
      <Alert
        severity={severity}
        action={
          <Stack direction='row' spacing={1} alignItems='center'>
            <Button
              color='inherit'
              variant='outlined'
              aria-disabled={retrying}
              onClick={() => {
                // aria-disabled (not disabled) preserves focus while the retry
                // is in flight, so guard recovery remains keyboard-friendly.
                if (!retrying) onRetry();
              }}
            >
              {retrying ? t('Retrying…') : (actionLabel ?? t('Retry'))}
            </Button>
            {secondaryAction}
          </Stack>
        }
      >
        {message}
        {attempt !== undefined && attempt > 1 && (
          <Box component='span' sx={visuallyHidden}>
            {t('Retry attempt failed.')} ({attempt})
          </Box>
        )}
      </Alert>
    </Box>
  );
};
